// Emergency appeals shown on the Offer Help page.
// Add a new entry here to publish a fresh appeal — no code changes required.
export const APPEALS = [
  {
    slug: "venezuela-earthquake-2026",
    overline: "EMERGENCY APPEAL · JUNE 2026",
    title: "Venezuela's Devastating 2026 Earthquakes",
    subtitle:
      "A nation in mourning, a world called to help — how you can support survivors of the twin M7.2 / M7.5 earthquakes that struck north-central Venezuela.",
    urgency: "critical",
    location: "Caracas · La Guaira · Northern Venezuela",
    intro:
      "In late June 2026, Venezuela experienced one of the deadliest natural disasters in its modern history. Two powerful earthquakes, measuring 7.2 and 7.5 in magnitude, struck within minutes of each other, devastating large areas of the country's north-central region, including Caracas, La Guaira, and neighboring communities.",
    lead:
      "Entire neighborhoods collapsed, hospitals were overwhelmed, roads and bridges were destroyed, and millions of people suddenly found themselves without homes, electricity, clean water, or access to medical care. As search-and-rescue operations continue and humanitarian organizations expand relief efforts, the disaster has become not only a national tragedy but also an international humanitarian emergency.",
    stats: [
      { k: "4,700+", v: "Confirmed dead" },
      { k: "17,000+", v: "People displaced" },
      { k: "69,000+", v: "Buildings damaged" },
      { k: "$37B", v: "Estimated economic loss" },
    ],
    sections: [
      {
        heading: "A catastrophic human toll",
        body: [
          "The human cost of the earthquakes has been staggering. Recent official and humanitarian reports indicate that more than 4,700 people have been confirmed dead, with thousands more injured and many still missing beneath collapsed buildings or unaccounted for in remote communities. Rescue teams continue searching damaged structures, meaning the final death toll may rise further.",
          "More than 17,000 people have been displaced, while hundreds of thousands have been affected by the destruction of homes, workplaces, schools, and essential public services. Survivors are now living in temporary shelters, community centers, tents, or with relatives as they wait for assistance.",
        ],
      },
      {
        heading: "Widespread destruction",
        body: [
          "The earthquakes caused catastrophic damage across northern Venezuela. Entire apartment buildings crumbled. Schools, hospitals, government buildings, and businesses suffered severe structural damage. Major highways cracked, bridges became unsafe, and landslides blocked transportation routes, making rescue operations extremely difficult.",
          "Current estimates suggest that more than 69,000 buildings have been damaged or destroyed, including homes, apartment complexes, medical facilities, and commercial buildings. Economic losses are estimated at approximately US$37 billion, making this one of the costliest disasters in Venezuela's history.",
        ],
      },
      {
        heading: "Infrastructure under enormous strain",
        body: [
          "Even before the earthquakes, Venezuela's infrastructure had been weakened by years of economic hardship and underinvestment. The disaster severely affected:",
        ],
        list: [
          "Hospitals and health clinics",
          "Water distribution systems",
          "Electrical power networks",
          "Telecommunications",
          "Roads, highways, and bridges",
          "Schools and universities",
          "Public transportation, ports, and logistics facilities",
        ],
        outro:
          "Many hospitals have been forced to operate with damaged buildings, limited electricity, shortages of medicine, and overcrowded emergency rooms. Medical teams continue to treat traumatic injuries while also working to prevent outbreaks of infectious diseases caused by overcrowded shelters and limited sanitation.",
      },
      {
        heading: "The human suffering behind the headlines",
        body: [
          "Statistics alone cannot capture the reality facing survivors. Thousands of families lost everything in a matter of seconds. Parents continue searching for missing children. Children have lost parents, siblings, teachers, and friends. Many elderly residents remain isolated after roads became impassable. Entire communities now rely on emergency food, bottled water, and temporary shelters.",
          "In many locations, people spent days sleeping outdoors because aftershocks made damaged buildings too dangerous to enter. Others have endured long waits for clean drinking water, fuel, and medical treatment while rescue teams worked around the clock. For many Venezuelans, the emotional trauma will last long after buildings are rebuilt.",
        ],
      },
      {
        heading: "International humanitarian response",
        body: [
          "The scale of the disaster prompted an extraordinary international response. The United Nations deployed emergency personnel and coordinated humanitarian operations alongside Venezuelan authorities and international partners. Countries throughout the Americas and Europe sent urban search-and-rescue teams, medical specialists, field hospitals, emergency supplies, heavy rescue equipment, water purification systems, emergency shelter materials, and food assistance.",
          "Organizations such as the Red Cross, World Central Kitchen, and numerous international relief agencies quickly mobilized to provide lifesaving support. The European Union announced additional humanitarian funding, while governments across Latin America, North America, and Europe contributed personnel and emergency assistance.",
        ],
      },
      {
        heading: "Rebuilding will take years",
        body: [
          "Although rescue operations remain underway in some areas, attention is gradually shifting toward recovery and reconstruction. Experts believe rebuilding Venezuela's damaged infrastructure will require years of sustained investment. Priority reconstruction efforts include:",
        ],
        list: [
          "Permanent housing for displaced families",
          "Rebuilding hospitals and healthcare facilities",
          "Repairing schools",
          "Restoring roads and bridges",
          "Rebuilding electrical systems",
          "Expanding access to clean water",
          "Strengthening building standards for future earthquakes",
        ],
        outro:
          "Many engineers have emphasized that reconstruction should focus not only on replacing damaged buildings but also on constructing safer, more earthquake-resistant communities.",
      },
      {
        heading: "Why financial donations matter most",
        body: [
          "While donations of clothing and supplies are generous, humanitarian organizations consistently recommend financial contributions whenever possible. Cash donations allow relief organizations to:",
        ],
        list: [
          "Purchase supplies locally.",
          "Deliver aid more quickly.",
          "Support local businesses.",
          "Avoid expensive international shipping.",
          "Respond as community needs change.",
          "Reach remote communities efficiently.",
        ],
        outro:
          "Even modest financial contributions can help provide meals, clean water, emergency shelter, medicines, and hygiene supplies for affected families.",
      },
    ],
    donation_partners: [
      {
        name: "UNICEF",
        description:
          "Supports children and families with emergency healthcare, nutrition, safe water, education, and child protection.",
        website: "https://www.unicef.org",
        response: "https://www.unicefusa.org/stories/venezuela-earthquakes-children-need-help-now",
      },
      {
        name: "International Rescue Committee (IRC)",
        description:
          "Providing emergency health services, protection, and humanitarian assistance for earthquake survivors.",
        website: "https://www.rescue.org",
        response: "https://www.rescue.org/article/how-help-survivors-earthquakes-venezuela",
      },
      {
        name: "Direct Relief",
        description:
          "Delivering emergency medicines, medical supplies, and equipment to affected healthcare facilities.",
        website: "https://www.directrelief.org",
        response: "https://www.directrelief.org/2026/06/venezuela-earthquake-caracas-damage/",
      },
      {
        name: "Global Empowerment Mission (GEM)",
        description:
          "Working with local partners to distribute food, hygiene supplies, shelter materials, and emergency aid.",
        website: "https://www.globalempowermentmission.org",
        response: "https://www.globalempowermentmission.org/mission/venezuela-earthquakes/",
      },
      {
        name: "United Nations Venezuela Humanitarian Fund",
        description:
          "Supports trusted humanitarian partners delivering emergency assistance throughout Venezuela.",
        website: "https://crisisrelief.un.org",
        response: "https://crisisrelief.un.org/en/donate-venezuela-crisis",
      },
      {
        name: "GiveDirectly",
        description:
          "Providing direct cash assistance to affected households, allowing families to prioritize their most urgent needs.",
        website: "https://www.givedirectly.org",
        response: "https://www.givedirectly.org/how-to-help-venezuela-earthquake/",
      },
    ],
    how_to_help: [
      "Donate to reputable humanitarian organizations.",
      "Share verified fundraising campaigns from trusted nonprofits.",
      "Encourage your workplace, school, or community group to organize a fundraiser.",
      "Volunteer with local organizations collecting aid where appropriate.",
      "Stay informed through reliable news sources and avoid spreading misinformation.",
      "Continue supporting recovery efforts even after media attention fades — rebuilding will take years.",
    ],
    outlook: {
      heading: "Looking ahead",
      body: [
        "The coming months will determine how successfully Venezuela can transition from emergency response to long-term recovery. Rebuilding homes, schools, hospitals, and public infrastructure will require not only financial resources but also sustained international cooperation and careful planning.",
        "For the thousands of families who have lost loved ones, homes, and livelihoods, recovery will not be measured only by reconstructed buildings. It will also be measured by the restoration of safety, opportunity, and hope.",
      ],
    },
    closing: {
      heading: "Final thoughts",
      body: [
        "Natural disasters often reveal both the fragility and resilience of humanity. Venezuela's 2026 earthquakes have caused immense suffering, leaving thousands dead, many more injured, and entire communities struggling to rebuild. Yet they have also inspired an extraordinary global response from rescue teams, medical professionals, volunteers, charitable organizations, and ordinary people determined to help.",
        "Every donation, every volunteer effort, and every act of compassion contributes to the long road toward recovery. While the headlines may eventually fade, the needs of survivors will remain. Continued international solidarity will be essential to helping Venezuela rebuild stronger, safer, and with renewed hope for the future.",
      ],
    },
    disclaimer:
      "The reported death toll and damage estimates are still evolving as rescue teams continue their work, so figures may change as authorities confirm additional casualties and complete damage assessments. The organizations listed are widely recognized humanitarian groups with active or established disaster-response capabilities.",
  },
];

export const findAppeal = (slug) => APPEALS.find((a) => a.slug === slug);
